import React, { useEffect } from 'react';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';

/**
 * ConfirmDialog Component
 * 
 * A reusable confirmation dialog that replaces native browser alerts/confirms
 * with a styled modal that matches the application's design system.
 * 
 * @param {boolean} isOpen - Controls dialog visibility
 * @param {function} onClose - Called when dialog is closed without confirmation
 * @param {function} onConfirm - Called when user confirms the action
 * @param {string} title - Dialog title (default: "Confirm Action")
 * @param {string} message - Dialog message/description
 * @param {string} confirmText - Text for confirm button (default: "Confirm")
 * @param {string} cancelText - Text for cancel button (default: "Cancel")
 * @param {string} type - Dialog type: 'danger', 'warning', 'info' (default: 'warning')
 */
const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
}) => {
  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle ESC key to close dialog
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Get icon and colors based on type
  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          icon: <FaExclamationTriangle className="text-5xl text-red-500" />,
          iconBg: 'bg-red-100',
          confirmButton: 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700',
        };
      case 'warning':
        return {
          icon: <FaExclamationTriangle className="text-5xl text-yellow-500" />,
          iconBg: 'bg-yellow-100',
          confirmButton: 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700',
        };
      case 'info':
      default:
        return {
          icon: <FaExclamationTriangle className="text-5xl text-blue-500" />,
          iconBg: 'bg-blue-100',
          confirmButton: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
        };
    }
  };

  const styles = getTypeStyles();

  return (
    // Backdrop
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
    >
      {/* Dialog Container */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90dvh] overflow-y-auto transform transition-all animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
          aria-label="Close dialog"
        >
          <FaTimes className="text-xl" />
        </button>

        {/* Dialog Content */}
        <div className="p-6 sm:p-8">
          {/* Icon */}
          <div className={`inline-flex items-center justify-center w-20 h-20 ${styles.iconBg} rounded-full mb-6`}>
            {styles.icon}
          </div>

          {/* Title */}
          <h2
            id="dialog-title"
            className="text-2xl font-display font-bold text-designer-dark mb-3"
          >
            {title}
          </h2>

          {/* Message */}
          <p
            id="dialog-description"
            className="text-gray-600 mb-8 leading-relaxed"
          >
            {message}
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 sm:px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-all transform hover:scale-[1.02] active:scale-95 text-sm sm:text-base"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`flex-1 px-4 sm:px-6 py-3 ${styles.confirmButton} text-white font-semibold rounded-lg transition-all transform hover:scale-[1.02] active:scale-95 shadow-lg text-sm sm:text-base`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
